/**
 * Shared learner-facing quiz summary card (lesson, course, level, tag panels).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { useAppLocale } from '../../providers/LocaleProvider';
import { PersianText } from '../shared/PersianText';

export interface LearnerQuizCardProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  questionCount: number;
  timeLimitMinutes?: number;
  passingScore: number;
  /** When true/false, appends "Required to pass" or "Optional". Omit when not applicable (e.g. optional-only course quizzes). */
  mandatoryForPassing?: boolean;
  userHasPassedQuiz?: boolean;
  userBestQuizScore?: number | null;
  onAction: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Defaults to Take quiz / Retake quiz */
  takeLabel?: string;
  retakeLabel?: string;
  className?: string;
  id?: string;
  isHighlighted?: boolean;
  /** Smaller description text for lesson / tag lesson panels. */
  compactDescription?: boolean;
}

export const LearnerQuizCard: React.FC<LearnerQuizCardProps> = ({
  title,
  description,
  questionCount,
  timeLimitMinutes,
  passingScore,
  mandatoryForPassing,
  userHasPassedQuiz,
  userBestQuizScore,
  onAction,
  takeLabel,
  retakeLabel,
  className = '',
  id,
  isHighlighted,
  compactDescription = false,
}) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const passed = userHasPassedQuiz === true;

  const timeLabel =
    timeLimitMinutes == null || timeLimitMinutes === 0
      ? t('quiz.noTimeLimitation')
      : t('quiz.timeLimitShort', { minutes: timeLimitMinutes });

  const passRequirementSuffix =
    mandatoryForPassing === true
      ? t('assessment.requiredToPassSuffix')
      : mandatoryForPassing === false
        ? t('assessment.optionalSuffix')
        : '';

  const take = takeLabel ?? t('quiz.takeQuiz');
  const retake = retakeLabel ?? t('quiz.retakeQuiz');

  return (
    <div
      id={id}
      dir={direction}
      className={`scroll-mt-24 rounded-xl border border-purple-200/80 bg-white/80 p-4 dark:border-purple-800/50 dark:bg-violet-950/20 ${
        passed ? 'border-b-[3px] border-b-emerald-500 dark:border-b-emerald-400' : ''
      } ${isHighlighted ? 'search-result-highlight' : ''} ${className}`.trim()}
    >
      <h3
        className={`flex items-center gap-1.5 text-start font-medium text-gray-900 dark:text-gray-100 ${
          isRtl ? 'flex-row-reverse justify-end' : ''
        }`}
      >
        {passed && (
          <CheckCircle2
            className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
            aria-label={t('quiz.passedShort')}
          />
        )}
        <span>{typeof title === 'string' ? <PersianText>{title}</PersianText> : title}</span>
      </h3>
      {description ? (
        <p
          className={
            compactDescription
              ? 'mt-0.5 text-start text-xs leading-snug text-violet-900/80 dark:text-violet-100/75'
              : 'mt-1 text-start text-sm text-violet-900/80 dark:text-violet-100/75'
          }
        >
          {typeof description === 'string' ? <PersianText>{description}</PersianText> : description}
        </p>
      ) : null}
      <p className="mt-2 text-start text-xs text-purple-800/75 dark:text-violet-200/70">
        {t('quiz.cardMetaLine', { count: questionCount, time: timeLabel, score: passingScore })}
        {passRequirementSuffix}
      </p>
      <button
        type="button"
        onClick={onAction}
        className="mt-3 w-full rounded-lg bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:from-violet-500 hover:to-purple-500"
      >
        {passed ? retake : take}
      </button>
      {passed && (
        <p className="mt-2 text-xs font-medium text-emerald-700 dark:text-emerald-400">
          {t('quiz.passedSuccessfullyMessage')}
          {userBestQuizScore !== undefined && userBestQuizScore !== null ? (
            <>
              {' '}
              {t('quiz.highestScoreLabel', { score: userBestQuizScore })}
            </>
          ) : null}
        </p>
      )}
    </div>
  );
};
